/* istanbul ignore file */
import { useContext } from "react";
import useBSTInsightsAPI from "../../../apiclients/api/bstinsightsapi";
import useAcquireToken from "../../AuthToken/Hooks/useAcquireToken";
import { BSTInsightsAppContext } from "../../../layouts/PageLayout";
import { RolesEnum } from "../../../utils/roles";
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ProjectDTO } from "../../../apiclients/bstinsights";

export const projectsForProjectThresholdsQueryKey = 'projectsForProjectThresholds';

export const useProjectsForThresholds = (searchValue: string = "") => {
  const { getTokenSilently } = useAcquireToken();
  const { getProjectsForProjectThresholdsPaged } = useBSTInsightsAPI();
  const { UserRoles } = useContext(BSTInsightsAppContext);
  const queryClient = useQueryClient();

  const hasProjectAdministrator = UserRoles.some(
    (item) => item.roleId === RolesEnum.ProjectAdministrator
  );
  const queryKey = [projectsForProjectThresholdsQueryKey, searchValue];
  const {
    data: projects = [],
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery({
    enabled: hasProjectAdministrator,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const accessToken = await getTokenSilently();

      const data = (await getProjectsForProjectThresholdsPaged(
        accessToken,
        pageParam,
        100,
        searchValue
      )) as ProjectDTO[];

      // When searching, update the main cache with new results to prevent refetching
      queryClient.setQueryData<InfiniteData<ProjectDTO>>(
        queryKey,
        (previousData) => {
          if (!previousData) return;

          return {
            ...previousData,
            pages: Array.from(
              new Map(
                [...previousData.pages, ...data].map((p) => [p.projectId, p])
              ).values()
            ),
          };
        }
      );

      return data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const lastPageItem = lastPage?.[lastPage.length - 1];
      return lastPageItem?.rowNumber
        ? Number(lastPageItem.rowNumber)
        : undefined;
    },
    select: (data) => data.pages.flatMap((page) => page),
  });

  return {
    projects,
    fetchNextPage,
    hasNextPage,
    isFetching,
  };
};
